import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createExclusion, searchCards } from './adminApi';

const SPORT_OPTIONS = [
  '', 'Baseball', 'Basketball', 'Football', 'Hockey', 'Pokemon',
  'Soccer', 'F1', 'Golf', 'Tennis',
  'One Piece', 'Yu-Gi-Oh', 'Marvel', 'Star Wars', 'Lorcana',
  'Magic: The Gathering', 'Garbage Pail Kids', 'VeeFriends', 'Riftbound',
  'Fortnite', 'Disney', 'Dragon Ball', 'Gundam', 'Simpsons',
];

const MODE_OPTIONS = [
  { value: '', label: 'All grading fees' },
  { value: 'standard', label: '$60' },
  { value: 'priority', label: '$80' },
  { value: 'express', label: '$199' },
  { value: 'super_express', label: '$349' },
];

const DEBOUNCE_MS = 300;

function money(value) {
  if (value === null || value === undefined) return '—';
  return `$${Number(value).toFixed(2)}`;
}

function percent(value) {
  if (value === null || value === undefined) return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
}

function AdminCardSearch({ meta, onViewCard, onExclusionCreated }) {
  const [queryText, setQueryText] = useState('');
  const [sport, setSport] = useState('');
  const [listFamily, setListFamily] = useState('');
  const [mode, setMode] = useState('');
  const [page, setPage] = useState(1);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removalReason, setRemovalReason] = useState('');
  const [notice, setNotice] = useState('');
  const [removing, setRemoving] = useState(false);

  const abortRef = useRef(null);
  const noticeTimer = useRef(null);

  // Same shape as QuickFlipSearch: a debounce, an AbortController, and a
  // request key so a slow earlier response cannot overwrite a newer one.
  const stateKey = `${queryText}|${sport}|${listFamily}|${mode}|${page}`;
  const latestKey = useRef(stateKey);

  const runSearch = useCallback(async (key) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');
    try {
      const data = await searchCards(
        { q: queryText, sport, list: listFamily, mode, page, per_page: 50 },
        controller.signal,
      );
      if (latestKey.current !== key) return;
      setResults(data);
    } catch (requestError) {
      if (requestError.name === 'AbortError') return;
      if (latestKey.current !== key) return;
      setError(requestError.message);
      setResults(null);
    } finally {
      if (latestKey.current === key) setLoading(false);
    }
  }, [queryText, sport, listFamily, mode, page]);

  useEffect(() => {
    latestKey.current = stateKey;
    const handle = setTimeout(() => runSearch(stateKey), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [stateKey, runSearch]);

  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
  }, []);

  const showNotice = (message) => {
    setNotice(message);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(''), 6000);
  };

  const resetFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;
    setRemoving(true);
    try {
      const result = await createExclusion(pendingRemoval.card_id, removalReason);
      if (result.status === 'already_excluded') {
        showNotice('That card was already on the exclusion list.');
      } else {
        const tables = Object.keys(result.deleted_from || {}).length;
        showNotice(
          `Removed ${pendingRemoval.description || pendingRemoval.card_id} from ${tables} `
          + `published ${tables === 1 ? 'list' : 'lists'}.`,
        );
      }
      setPendingRemoval(null);
      setRemovalReason('');
      if (onExclusionCreated) onExclusionCreated();
      runSearch(latestKey.current);
    } catch (removalError) {
      setError(removalError.message);
    } finally {
      setRemoving(false);
    }
  };

  const rows = results?.data || [];
  const total = results?.total || 0;
  const totalPages = Math.max(Math.ceil(total / (results?.per_page || 50)), 1);

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input
          className="admin-input admin-input--grow"
          type="search"
          value={queryText}
          onChange={resetFilter(setQueryText)}
          placeholder="Search description, player, or card ID"
          aria-label="Search cards"
        />
        <select className="admin-input" value={sport} onChange={resetFilter(setSport)} aria-label="Sport or category">
          {SPORT_OPTIONS.map((option) => (
            <option key={option || 'all'} value={option}>{option || 'All sports & categories'}</option>
          ))}
        </select>
        <select className="admin-input" value={listFamily} onChange={resetFilter(setListFamily)} aria-label="List">
          <option value="">All lists</option>
          {(meta?.list_families || []).map((family) => (
            <option key={family.value} value={family.value}>{family.label}</option>
          ))}
        </select>
        <select className="admin-input" value={mode} onChange={resetFilter(setMode)} aria-label="Grading fee">
          {MODE_OPTIONS.map((option) => (
            <option key={option.value || 'all'} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {notice && <p className="admin-notice" role="status">{notice}</p>}
      {error && <p className="admin-error" role="alert">{error}</p>}

      <p className="admin-meta-line">
        {loading ? 'Searching…' : `${total} ${total === 1 ? 'card' : 'cards'} across ${results?.tables_searched?.length || 0} tables`}
      </p>

      <div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Card</th>
              <th scope="col">Sport</th>
              <th scope="col">Raw</th>
              <th scope="col">PSA 10 (30d)</th>
              <th scope="col">Gem rate</th>
              <th scope="col">COIN</th>
              <th scope="col">On lists</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="admin-empty">No cards match those filters.</td></tr>
            )}
            {rows.map((card) => (
              <tr key={card.card_id}>
                <td>
                  <span className="admin-card-description">{card.description || '(no description)'}</span>
                  <span className="admin-card-sub">
                    {card.player || 'Unknown player'}
                    {card.number ? ` · #${card.number}` : ''}
                    {' · '}
                    <code>{card.card_id}</code>
                  </span>
                </td>
                <td>{card.sport || '—'}</td>
                <td>{money(card.raw_price)}</td>
                <td>{money(card.price_30d)}</td>
                <td>{percent(card.card_gem_rate)}</td>
                <td>{card.coin_score === null || card.coin_score === undefined ? '—' : Number(card.coin_score).toFixed(2)}</td>
                <td>
                  <span className="admin-badge-row">
                    {(card.appears_on || []).map((entry) => (
                      <span key={entry.source_table} className="admin-badge" title={entry.source_table}>
                        {entry.list_family === 'vault' ? 'Elite' : entry.list_family === 'goat' ? 'GOAT' : 'Sports'}
                        {' '}
                        {entry.fee_label}
                      </span>
                    ))}
                  </span>
                </td>
                <td className="admin-actions">
                  <button type="button" className="admin-button" onClick={() => onViewCard(card.card_id)}>
                    Raw data
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button--danger"
                    onClick={() => { setPendingRemoval(card); setRemovalReason(''); }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="admin-pager">
          <button type="button" className="admin-button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" className="admin-button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {pendingRemoval && (
        <div
          className="admin-modal-overlay"
          onClick={() => !removing && setPendingRemoval(null)}
          role="presentation"
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-remove-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="admin-remove-title">Remove this card from every list?</h3>
            <p className="admin-modal-card">{pendingRemoval.description || pendingRemoval.card_id}</p>
            <p className="admin-modal-detail">
              It will be deleted from all {pendingRemoval.list_count} published
              {pendingRemoval.list_count === 1 ? ' list' : ' lists'} immediately and kept off
              future pipeline runs. You can restore it from the Exclusions tab.
            </p>
            <label className="admin-label" htmlFor="admin-removal-reason">Reason (optional)</label>
            <input
              id="admin-removal-reason"
              className="admin-input admin-input--block"
              value={removalReason}
              onChange={(event) => setRemovalReason(event.target.value)}
              placeholder="e.g. Unreliable card pricing"
            />
            <div className="admin-modal-actions">
              <button type="button" className="admin-button" disabled={removing} onClick={() => setPendingRemoval(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-button admin-button--danger"
                disabled={removing}
                onClick={confirmRemoval}
              >
                {removing ? 'Removing…' : 'Remove card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCardSearch;
